import { defineStore } from 'pinia'
import { computed, inject, ref } from 'vue'
import type { RequestRouter } from '@/request/requestRouter'
import { RequestAction } from '@/request/requestRouter'
import type { ModerationError, ModerationRule } from '@/types'
import { ModerationRulePromptType, ModerationRuleType } from '@/types'

export const useModerationStore = defineStore('moderation', () => {
	const apiWords = ref<string[]>([])
	const request = inject<RequestRouter>('request')

	async function initialize(): Promise<void> {
		apiWords.value = await request?.exec(RequestAction.GetBannedWords) ?? []
	}

	const rules = computed((): ModerationRule[] => [
		{
			in: ['gay', 'homo', 'homosexual'],
			out: ['yaoi', 'bara'],
			type: ModerationRuleType.Suggestion,
			prompt: ModerationRulePromptType.All,
		},
		{
			in: ['lesbian', 'lesb'],
			out: ['yuri'],
			type: ModerationRuleType.Suggestion,
			prompt: ModerationRulePromptType.All,
		},
		{
			in: ['animal'],
			out: ['furry', 'anthro'],
			type: ModerationRuleType.Suggestion,
			prompt: ModerationRulePromptType.All,
		},
		{
			in: [
				...apiWords.value,
			],
			out: [],
			type: ModerationRuleType.Forbidden,
			prompt: ModerationRulePromptType.Positive,
		},
		{
			in: [
				'r/\\b(1[89]|2[0-9]|30)\\s*(?:y(?:ears?)?(?:\\s*old)?|yo)\\b',
				'mature',
				'adult',
				'grown up',
			],
			out: [],
			type: ModerationRuleType.Forbidden,
			prompt: ModerationRulePromptType.Negative,
		},
	])

	function findWord(prompt: string, word: string): string[] {
		const foundWords: string[] = []

		if (word.startsWith('+')) {
			const cleanWord = word.replace('+', '')
			const wordsToCheck = cleanWord.split(' ')

			let allWordsPresent = true
			for (const singleWord of wordsToCheck) {
				const cleanSingleWord = singleWord.toLowerCase()
				const spacedWord = cleanSingleWord.split('').join('[^a-z\\s<>]*')
				const regex = new RegExp(`(?<![a-zA-Z])${spacedWord}(?![a-zA-Z])`, 'gi')
				if (!regex.test(prompt)) {
					allWordsPresent = false
					break
				}
			}

			if (allWordsPresent) {
				for (const singleWord of wordsToCheck) {
					foundWords.push(singleWord.toLowerCase())
				}
			}
		}
		else {
			const lowercaseWord = word.toLowerCase()
			const spacedWord = lowercaseWord.split('').join('[^a-z\\s<>]*')
			const lastChar = lowercaseWord[lowercaseWord.length - 1]
			const regex = new RegExp(`(?<![a-zA-Z])${spacedWord}(?:${lastChar}{1,4}|s{0,3})(?![a-zA-Z])`, 'gi')

			let match = regex.exec(prompt)
			while (match !== null) {
				foundWords.push(lowercaseWord)
				match = regex.exec(prompt)
			}
		}

		return foundWords
	}

	function checkPrompt(prompt: string, type: 'positive' | 'negative' = 'positive'): ModerationError<Array<string[]> | string[]>[] {
		const errorMap = new Map<string, ModerationError<Array<string[]> | string[]>>()

		rules.value.forEach((rule: ModerationRule) => {
			if (type === 'positive' && rule.prompt === ModerationRulePromptType.Negative) {
				return
			}
			if (type === 'negative' && rule.prompt === ModerationRulePromptType.Positive) {
				return
			}

			const foundWords: string[] = []
			rule.in.forEach((word: string) => {
				if (word.startsWith('r/')) {
					const regexStr = word.slice(2)
					const regex = new RegExp(regexStr, 'gi')
					let match = regex.exec(prompt)
					while (match !== null) {
						foundWords.push(match[0])
						match = regex.exec(prompt)
					}
				}
				else {
					const sanitizedPrompt = prompt/* .replace(/[^a-z\s<>]/gi, ' ') */.replace(/\s+/g, ' ').trim().toLowerCase()
					let suggestionExists = false
					if (rule.type === ModerationRuleType.Suggestion) {
						suggestionExists = rule.out.reduce((acc, suggestion) => {
							const suggestionRegex = new RegExp(`\\b${suggestion}\\b`, 'i')
							return acc || suggestionRegex.test(sanitizedPrompt)
						}, false)
					}
					if (!suggestionExists) {
						foundWords.push(...findWord(sanitizedPrompt, word))
					}
				}
			})

			if (foundWords.length > 0) {
				const key = rule.type.toString()
				if (!errorMap.has(key)) {
					errorMap.set(key, {
						type: rule.type,
						words: [],
						suggestions: undefined,
					})
				}
				if (rule.type !== ModerationRuleType.Suggestion) {
					const error = errorMap.get(key)! as ModerationError<string[]>
					error!.words.push(...foundWords)
					if (error.suggestions === undefined) {
						error.suggestions = rule.out.length > 0 ? [...rule.out] : undefined
					}
				}
				else {
					const error = errorMap.get(key)! as ModerationError<Array<string[]>>
					error!.words.push(foundWords)
					if (error.suggestions === undefined) {
						error.suggestions = []
					}
					error.suggestions.push([...rule.out])
				}
			}
		})

		// Nettoyer les doublons dans les mots pour Forbidden
		errorMap.forEach((error) => {
			if (error.type === ModerationRuleType.Forbidden) {
				error.words = [...new Set(error.words as string[])]
			}
		})

		return Array.from(errorMap.values())
	}

	function getErrorMessage(error: ModerationError<Array<string[]> | string[]>): string {
		switch (error.type) {
			case ModerationRuleType.Forbidden:
				return 'Ce prompt contient des mots interdits.'
			case ModerationRuleType.Suggestion:
				return `Nous vous suggérons fortement d'utiliser ${error.suggestions?.join(', ')} au lieu de "${error.words[0]}".`
			default:
				return 'Erreur de modération inconnue.'
		}
	}

	return {
		// State
		apiWords,
		// Getters
		rules,
		// Actions
		checkPrompt,
		getErrorMessage,
		initialize,
	}
})
